import { saveAs } from 'file-saver';
import { DEBUG } from '../settings';

/**
 * Formats a number to have exactly 2 decimal places
 * @param {number} number - The number to format
 * @returns {number} The formatted number
 */
export const formatToTwoDecimals = (number) => {
  if (typeof number === "number") {
    return Math.trunc(number * 100) / 100;
  } else {
    return Math.trunc(parseFloat(number) * 100) / 100;
  }
};

/**
 * Gets the export filename with the correct extension
 * @param {string} baseName - The base filename
 * @param {string} extension - The file extension
 * @returns {string} The complete filename
 */
export const getExportFilename = (baseName, extension) => {
  // Remove common audio file extensions before adding the new extension
  const cleanBaseName = baseName
    .replace(/\.mp3$/i, '')
    .replace(/\.wav$/i, '')
    .replace(/\.m4a$/i, '')
    .replace(/\.aac$/i, '')
    .replace(/\.ogg$/i, '')
    .replace(/\.flac$/i, '')
    .replace(/\.wma$/i, '')
    .replace(/\.aiff$/i, '')
    .replace(/\.webm$/i, '')
    .replace(/\.mp4$/i, '')
    .replace(/\.avi$/i, '')
    .replace(/\.mov$/i, '')
    .replace(/\.mkv$/i, '');
  
  return `${cleanBaseName}.${extension}`;
};

/**
 * Exports a file with the given content
 * @param {string} fileName - The name of the file
 * @param {string|Blob} content - The content to export
 * @param {string} type - The MIME type of the file
 */
export const exportFile = (fileName, content, type = 'text/plain;charset=utf-8') => {
  const blob = new Blob([content], { type });
  saveAs(blob, fileName);
};

/**
 * Handles API errors and sends error reports
 * @param {Error} error - The error object
 * @param {string} functionName - The name of the function where the error occurred
 * @param {string} recordId - The record ID associated with the error
 * @param {Object} apiClient - The API client instance
 */
export const handleApiError = (error, functionName, recordId, apiClient) => {
  let errorMessage = `Error in ${functionName}`;
  
  if (error.response && error.response.data) {
    if (error.response.data.constructor === ArrayBuffer) {
      const decoder = new TextDecoder();
      const dataDecoded = JSON.parse(decoder.decode(error.response.data));
      errorMessage = dataDecoded.message;
    } else {
      errorMessage = error.response.data.message;
    }
  }

  apiClient.post('senderroremail', 
    `Error in ${functionName}, recordId: ${recordId}, errorMessage: ${errorMessage}`,
    {
      params: {
        errorType: 'subtitleScriptError',
      }
    }
  );

  if (DEBUG) {
    console.log(`Error in ${functionName} function: `, `recordId: ${recordId}, errorMessage: ${errorMessage}`);
  }
};

/**
 * Removes extra spaces and newlines from text
 * @param {string} text - The text to clean
 * @returns {string} The cleaned text
 */
export const cleanText = (text) => {
  const removedNewLines = text.replace(/\n/g, ' ');
  const removedExtraSpaces = removedNewLines.split(' ').filter(a => a !== '').join(' ');
  return removedExtraSpaces.trim();
};

/**
 * Calculates the duration between two timestamps
 * @param {number} startTime - The start time in seconds
 * @param {number} endTime - The end time in seconds
 * @returns {string} The formatted duration
 */
export const calculateDuration = (startTime, endTime) => {
  const duration = endTime - startTime;
  return formatToTwoDecimals(duration);
};

/**
 * Checks if a value is empty (null, undefined, empty string, or empty array)
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}; 